import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import ManhwaCard from "@/components/feature/ManhwaCard";
import { getManhwaBySlug, latestManhwas } from "@/mocks/manhwa";

export default function ManhwaDetail() {
  const { slug } = useParams<{ slug: string }>();
  const manhwa = getManhwaBySlug(slug || "");

  if (!manhwa) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
            <h1 className="mt-4 text-xl font-bold text-foreground-900">漫画未找到</h1>
            <p className="mt-2 text-foreground-500">您查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: manhwa.categoryName, href: `/category/${manhwa.category}` },
    { label: manhwa.title },
  ];

  const relatedManhwas = latestManhwas
    .filter((m) => m.id !== manhwa.id)
    .slice(0, 6);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: manhwa.title,
    author: { "@type": "Person", name: manhwa.author },
    description: manhwa.description,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: manhwa.rating,
      bestRating: "5",
      ratingCount: Math.floor(manhwa.views / 10),
    },
    genre: manhwa.categoryName,
    url: `https://www.manhwadaquan.com/manhwa/${manhwa.slug}`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.manhwadaquan.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: manhwa.categoryName,
        item: `https://www.manhwadaquan.com/category/${manhwa.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: manhwa.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Detail Header */}
          <div className="mt-8 flex flex-col md:flex-row gap-8 md:gap-10">
            <div className="w-48 md:w-56 flex-shrink-0 mx-auto md:mx-0">
              <div className="rounded-xl overflow-hidden bg-background-200">
                <img
                  src={manhwa.cover}
                  alt={manhwa.title}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                  {manhwa.categoryName}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    manhwa.status === "已完结"
                      ? "bg-secondary-500 text-white"
                      : "bg-accent-500 text-white"
                  }`}
                >
                  {manhwa.status}
                </span>
                {manhwa.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                {manhwa.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">作者：{manhwa.author}</p>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400" />
                  <span className="font-semibold text-foreground-900">{manhwa.rating}</span>
                  <span className="text-foreground-500">/ 5.0</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-eye-line" />
                  <span>{formatViews(manhwa.views)} 次阅读</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-file-list-3-line" />
                  <span>共 {manhwa.chapters} 话</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-time-line" />
                  <span>更新于 {manhwa.updatedAt}</span>
                </div>
              </div>

              <div className="mt-5 p-4 rounded-xl bg-background-100/70 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manhwa.description}
                </p>
              </div>

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button className="inline-flex items-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-sm cursor-pointer">
                  <i className="ri-play-circle-line mr-2 text-lg" />
                  开始阅读第一话
                </button>
                <button className="inline-flex items-center px-5 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm cursor-pointer">
                  <i className="ri-heart-line mr-2" />
                  加入收藏
                </button>
                <button className="inline-flex items-center px-5 py-3 bg-background-100 hover:bg-background-200 text-foreground-600 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm cursor-pointer">
                  <i className="ri-share-line mr-2" />
                  分享
                </button>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mt-12 mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-serif mb-4">
              <a id="chapters">章节列表</a>
            </h2>
            <div className="border border-background-200/70 rounded-xl overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                {Array.from({ length: Math.min(manhwa.chapters, 30) }, (_, i) => {
                  const chapterNum = manhwa.chapters - i;
                  return (
                    <div
                      key={chapterNum}
                      className="flex items-center justify-between px-5 py-3 border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-800">
                          第{chapterNum}话
                        </span>
                        {chapterNum === manhwa.chapters && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                            NEW
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{manhwa.updatedAt}</span>
                        <i className="ri-arrow-right-s-line text-lg" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Related Manhwas */}
          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-serif mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {relatedManhwas.map((m) => (
                <ManhwaCard key={m.id} {...m} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}

function formatViews(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + "万";
  }
  return num.toString();
}